/**
 * RiderHoursPage · v2.8+E301
 * ────────────────────────────────────────────────────────────
 * Admin panel to control the RIDER portal booking-form Hours of Operation.
 * Edit open/close time, active days, and a manual override (force open /
 * force closed) for holidays & special events. Saves take effect immediately
 * and push a real-time WebSocket frame so open rider forms flip instantly.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  Clock, ArrowLeft, Loader2, RefreshCw, CheckCircle2, XCircle,
  Calendar, Zap, Lock, Save,
} from "lucide-react";

const DAYS = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" }, { v: 6, label: "Sun" },
];
const OVERRIDES = [
  { v: "scheduled", label: "Scheduled", desc: "Follow the hours below", Icon: Clock, accent: "sky" },
  { v: "force_open", label: "Force Open", desc: "Always open (override)", Icon: Zap, accent: "emerald" },
  { v: "force_closed", label: "Force Closed", desc: "Always closed (holiday)", Icon: Lock, accent: "rose" },
];

export default function RiderHoursPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [openTime, setOpenTime] = useState("10:00");
  const [closeTime, setCloseTime] = useState("03:00");
  const [businessOpenTime, setBusinessOpenTime] = useState("08:00");
  const [days, setDays] = useState(new Set([0, 1, 2, 3, 4, 5, 6]));
  const [override, setOverride] = useState("scheduled");
  const [updatedBy, setUpdatedBy] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/rider-portal/hours");
      setStatus(data.status);
      if (!silent) {
        // Only hydrate the editable form on a full (non-poll) load so we don't
        // stomp unsaved edits while the background poll refreshes the badge.
        setOpenTime(data.config.open_time || "10:00");
        setCloseTime(data.config.close_time || "03:00");
        setBusinessOpenTime(data.config.business_open_time || "08:00");
        setDays(new Set(data.config.active_days || []));
        setOverride(data.config.override_mode || "scheduled");
      }
      setUpdatedBy(data.config.updated_by);
    } catch (e) {
      if (!silent) toast.error("Could not load Hours of Operation.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Keep the live status badge fresh (also catches the scheduled 10AM/3AM flip).
  useEffect(() => {
    const id = setInterval(() => load(true), 20000);
    return () => clearInterval(id);
  }, [load]);

  const toggleDay = (v) => {
    setDays((prev) => {
      const next = new Set(prev);
      next.has(v) ? next.delete(v) : next.add(v);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/rider-portal/hours", {
        open_time: openTime,
        close_time: closeTime,
        business_open_time: businessOpenTime,
        active_days: Array.from(days).sort((a, b) => a - b),
        override_mode: override,
      });
      setStatus(data.status);
      toast.success("Hours saved — the rider form updates instantly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const isOpen = status?.is_open;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6" data-testid="rider-hours-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sky-500/10 border border-sky-500/40 flex items-center justify-center text-sky-400">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Rider Portal Hours</h1>
              <div className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">
                Hours of Operation · online booking form
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}
              data-testid="rider-hours-refresh" className="bg-zinc-950 border-zinc-700 h-9 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")}
              data-testid="rider-hours-back" className="bg-zinc-950 border-zinc-700 h-9 text-xs">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Back to Console
            </Button>
          </div>
        </div>

        {/* Live status */}
        <Card data-testid="rider-hours-status" className={`p-5 border ${isOpen ? "bg-emerald-500/5 border-emerald-500/40" : "bg-rose-500/5 border-rose-500/40"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {isOpen ? <CheckCircle2 className="h-8 w-8 text-emerald-400" /> : <XCircle className="h-8 w-8 text-rose-400" />}
              <div>
                <div className={`text-xl font-black ${isOpen ? "text-emerald-300" : "text-rose-300"}`} data-testid="rider-hours-open-state">
                  {status ? (isOpen ? "OPEN — accepting rider bookings" : "CLOSED — booking form disabled") : "…"}
                </div>
                <div className="text-xs text-zinc-400">
                  {status?.hours_label} · {status?.days_label}
                  {status?.override_mode && status.override_mode !== "scheduled" && (
                    <span className="ml-2 text-amber-300 font-bold uppercase">· Manual override: {status.override_mode.replace("_", " ")}</span>
                  )}
                </div>
                {!isOpen && status?.next_open_label && (
                  <div className="text-xs text-zinc-300 mt-0.5">Reopens {status.next_open_label}</div>
                )}
              </div>
            </div>
            {status?.now_ct && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Now ({status.tz_abbrev})</div>
                <div className="text-sm font-mono text-zinc-200" data-testid="rider-hours-now">
                  {new Date(status.now_ct).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: status.timezone })}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Editor */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-6">
          {/* Online booking window — the GATE that enables/disables the rider form + support chat */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold block mb-1.5">Online booking opens (CT)</label>
              <input type="time" value={openTime} onChange={(e) => setOpenTime(e.target.value)}
                data-testid="rider-hours-open-time"
                className="w-full h-10 px-3 rounded-md bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold block mb-1.5">Close time (CT)</label>
              <input type="time" value={closeTime} onChange={(e) => setCloseTime(e.target.value)}
                data-testid="rider-hours-close-time"
                className="w-full h-10 px-3 rounded-md bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm" />
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 -mt-3">
            This <span className="text-zinc-300">online booking window</span> enables/disables the rider portal form + support chat. A close time earlier than the open time runs <span className="text-zinc-300">overnight</span> (e.g. 10:00 AM → 3:00 AM next morning).
          </div>

          {/* Public business (phone) hours — DISPLAY ONLY, separate from the online gate */}
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <label className="text-[11px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1.5 mb-1.5">
              <Clock className="h-3.5 w-3.5" /> Public business hours — open time (CT)
            </label>
            <input type="time" value={businessOpenTime} onChange={(e) => setBusinessOpenTime(e.target.value)}
              data-testid="rider-hours-business-open-time"
              className="w-full sm:w-48 h-10 px-3 rounded-md bg-zinc-950 border border-amber-500/40 text-zinc-100 text-sm" />
            <div className="text-[11px] text-zinc-400 mt-2">
              Display only — shown as your "Hours of Operation" on the homepage banner, footer, contact page, and rider login (uses the same close time above). Does <span className="text-zinc-200">not</span> affect when online booking opens.
            </div>
          </div>

          {/* Active days */}
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1.5 mb-2">
              <Calendar className="h-3.5 w-3.5" /> Active days
            </label>
            <div className="flex flex-wrap gap-2" data-testid="rider-hours-days">
              {DAYS.map((d) => {
                const on = days.has(d.v);
                return (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                    data-testid={`rider-hours-day-${d.v}`}
                    className={`h-9 w-14 rounded-md text-xs font-bold uppercase tracking-wider border transition-colors ${on ? "bg-sky-500/20 border-sky-500/60 text-sky-200" : "bg-zinc-950 border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Override */}
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold block mb-2">Manual override</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="rider-hours-override">
              {OVERRIDES.map((o) => {
                const on = override === o.v;
                const accent = { sky: "border-sky-500/60 bg-sky-500/10 text-sky-200", emerald: "border-emerald-500/60 bg-emerald-500/10 text-emerald-200", rose: "border-rose-500/60 bg-rose-500/10 text-rose-200" }[o.accent];
                return (
                  <button key={o.v} type="button" onClick={() => setOverride(o.v)}
                    data-testid={`rider-hours-override-${o.v}`}
                    className={`p-3 rounded-lg border text-left transition-colors ${on ? accent : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}>
                    <div className="flex items-center gap-1.5 font-bold text-sm"><o.Icon className="h-4 w-4" />{o.label}</div>
                    <div className="text-[11px] opacity-80 mt-0.5">{o.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-zinc-800">
            <div className="text-[11px] text-zinc-500">
              Changes take effect <span className="text-zinc-300">immediately</span> and update the rider form in real time.
              {updatedBy && <span className="ml-1">Last changed by {updatedBy}.</span>}
            </div>
            <Button onClick={save} disabled={saving || days.size === 0}
              data-testid="rider-hours-save"
              className="h-10 bg-sky-500 hover:bg-sky-400 text-black font-bold disabled:opacity-40">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save changes
            </Button>
          </div>
          {days.size === 0 && <div className="text-[11px] text-rose-400 -mt-3">Select at least one active day.</div>}
        </Card>
      </div>
    </div>
  );
}
